import { twyneChainData, twyneConfigFor } from '@1delta/data-sdk'
import { twyneKeyParts } from '../../public-data/twyne/convertPublic'
import {
  TwyneATokenWrapperAbi,
  TwyneCollateralVaultAbi,
  TwyneCollateralVaultFactoryAbi,
} from '@1delta/abis'
import { multicallRetryUniversal } from '@1delta/providers'
import { Call } from '../../../utils/multicall'

/**
 * User-data call build for Twyne.
 *
 * A position is a CONTRACT the borrower deployed, and a borrower may own many
 * per market — so positions are SUB-ACCOUNTS keyed by the collateral vault
 * address (the Frankencoin shape), not one-per-user-per-market.
 *
 * DISCOVERY-FIRST, in two rounds, because a vault's market cannot be known
 * without reading it:
 *
 *   round 1  factory.getCollateralVaults(account)  — every vault the account
 *            CREATED, in creation order. One call, no log scan.
 *   round 2  per vault: intermediateVault(), targetAsset(), borrower()
 *            — the immutable market mapping, plus the CURRENT owner.
 *
 * Only the STATE reads then go into the shared batch, so a user with no Twyne
 * position costs exactly one call.
 *
 * **`borrower()` is not redundant.** Liquidation on Twyne is BY INHERITANCE:
 * the liquidator becomes the vault's `borrower`, but the factory's array still
 * lists the vault under whoever CREATED it. Without this check a liquidated
 * user keeps seeing the position as theirs. The mirror image is a real gap we
 * accept: a liquidator who inherited a vault will NOT find it through
 * `getCollateralVaults`, because they never created it — recovering those needs
 * the `T_SetCollateralVaultLiquidated` event, which is out of scope for a
 * per-request read.
 */

const DISCOVERY_TTL_MS = 60_000

/** State reads per vault. Keep in lock-step with the parser's slicer. */
export const TWYNE_READS_PER_VAULT = 7

export interface TwyneVaultRef {
  /** The collateral vault contract — the position id. */
  vault: string
  /** Resolves the market: `TWYNE_<chainId>_<IV>_<TARGET_ASSET>`. */
  intermediateVault: string
  targetAsset: string
  /**
   * The market's collateral RECEIPT token (an Euler eToken, or Twyne's aToken
   * wrapper), taken from the roster — the share price must be read off IT. A
   * collateral vault is neither ERC-20 nor ERC-4626: its `fallback()` reverts
   * `T_CV_OperationDisabled`.
   */
  collateralAsset?: string
  /**
   * Receipt → underlying scale (`convertToAssets(1e18)`), READ not assumed.
   *
   * Resolved HERE rather than in the shared user-data batch, and that placement
   * is the point: the vault does not implement it (it is neither ERC-20 nor
   * ERC-4626), the receipt does — and the shared batch is a sharded, thousands-
   * of-calls affair whose per-call failures come back as `null`, which the
   * parser cannot distinguish from a legitimate 1:1. Reading it on the
   * discovery round means a lost read degrades to 1:1 only if THIS call fails,
   * and it is cached with the discovery for a value that moves like a share
   * price rather than like a balance.
   */
  collateralToUnderlying?: bigint
}

export interface TwyneDiscovery {
  vaults: TwyneVaultRef[]
  at: number
}

const discoveryCache = new Map<string, TwyneDiscovery>()
const discoveryKey = (chainId: string, lender: string, account: string) =>
  `${chainId}:${lender}:${account.toLowerCase()}`

export function getCachedTwyneDiscovery(
  chainId: string,
  lender: string,
  account: string,
): TwyneDiscovery | undefined {
  return discoveryCache.get(discoveryKey(chainId, lender, account))
}

/** Test seam — the caches are module-level and would leak across cases. */
export function __resetTwyneUserCaches(): void {
  discoveryCache.clear()
}

const lower = (a: string) => String(a).toLowerCase()
const isAddress = (v: any): v is string => typeof v === 'string' && /^0x[0-9a-fA-F]{40}$/.test(v)

async function discover(
  chainId: string,
  lender: string,
  account: string,
): Promise<TwyneDiscovery> {
  const key = discoveryKey(chainId, lender, account)
  const cached = discoveryCache.get(key)
  if (cached && Date.now() - cached.at < DISCOVERY_TTL_MS) return cached

  const cfg = twyneConfigFor(lender, chainId)
  const empty: TwyneDiscovery = { vaults: [], at: Date.now() }
  if (!cfg?.collateralVaultFactory) return empty

  const [listRaw] = (await multicallRetryUniversal({
    chain: chainId,
    calls: [
      {
        address: cfg.collateralVaultFactory,
        name: 'getCollateralVaults',
        args: [account],
      },
    ],
    abi: TwyneCollateralVaultFactoryAbi,
    allowFailure: true,
  })) as any[]

  const created: string[] = Array.isArray(listRaw) ? listRaw.filter(isAddress).map(lower) : []
  if (created.length === 0) {
    discoveryCache.set(key, empty)
    return empty
  }

  const refs = (await multicallRetryUniversal({
    chain: chainId,
    calls: created.flatMap((v) => [
      { address: v, name: 'intermediateVault', args: [] },
      { address: v, name: 'targetAsset', args: [] },
      { address: v, name: 'borrower', args: [] },
    ]),
    abi: TwyneCollateralVaultAbi,
    allowFailure: true,
  })) as any[]

  // iv|target -> the market's collateral receipt token. Doubles as the curated
  // roster membership check below.
  //
  // SCOPED TO THE QUERIED MARKET when the key names one. The factory lists an
  // account's vaults across every market, so an unscoped map made all eight
  // Twyne queries read the SAME vault — 72 duplicate calls where 9 were needed,
  // in one `aggregate3` whose tail then ran out of gas. That surfaced as the
  // last reads of every group coming back as failures, at a cut-off point that
  // moved between runs, and the parser reads a failed share-price read as 1:1.
  // Filtering here is what makes each query cost what it should.
  const queried = twyneKeyParts(lender)
  const known = new Map<string, string | undefined>(
    (twyneChainData(lender, chainId)?.markets ?? [])
      .filter(
        (m) =>
          !queried ||
          (lower(m.intermediateVault) === lower(queried.intermediateVault) &&
            lower(m.targetAsset) === lower(queried.targetAsset)),
      )
      .map(
        (m) =>
          [
            `${lower(m.intermediateVault)}|${lower(m.targetAsset)}`,
            m.collateralAsset,
          ] as const,
      ),
  )

  const vaults: TwyneVaultRef[] = []
  created.forEach((vault, i) => {
    const iv = refs[i * 3]
    const target = refs[i * 3 + 1]
    const owner = refs[i * 3 + 2]
    if (!isAddress(iv) || !isAddress(target)) return
    // Ownership moved — the account was liquidated out of this vault. Dropping
    // it is the point of reading `borrower()`; see the header.
    if (isAddress(owner) && lower(owner) !== lower(account)) return
    // FAIL CLOSED on a market that is not in the curated roster: without it
    // there is no public row to attach the position to, and a position with no
    // market renders as an orphan rather than as a warning.
    const marketKey = `${lower(iv)}|${lower(target)}`
    if (!known.has(marketKey)) return
    vaults.push({
      vault,
      intermediateVault: lower(iv),
      targetAsset: lower(target),
      collateralAsset: known.get(marketKey),
    })
  })

  // The receipt's share price, once per DISTINCT receipt token.
  const receipts = [
    ...new Set(vaults.map((v) => v.collateralAsset).filter((a): a is string => !!a)),
  ]
  if (receipts.length > 0) {
    const scales = (await multicallRetryUniversal({
      chain: chainId,
      calls: receipts.map((address) => ({
        address,
        name: 'convertToAssets',
        args: [10n ** 18n],
      })),
      abi: TwyneATokenWrapperAbi,
      allowFailure: true,
    })) as any[]
    const byReceipt = new Map<string, bigint>()
    receipts.forEach((address, i) => {
      const v = scales[i]
      const scale =
        typeof v === 'bigint' ? v : typeof v === 'string' && /^\d+$/.test(v) ? BigInt(v) : undefined
      if (scale && scale > 0n) byReceipt.set(lower(address), scale)
    })
    for (const v of vaults) {
      const scale = v.collateralAsset ? byReceipt.get(lower(v.collateralAsset)) : undefined
      if (scale) v.collateralToUnderlying = scale
    }
  }

  const result: TwyneDiscovery = { vaults, at: Date.now() }
  discoveryCache.set(key, result)
  return result
}

/**
 * State reads per discovered vault, in a fixed order the parser mirrors:
 *
 *   0 totalAssetsDepositedOrReserved  collateral INCLUDING reserved credit
 *   1 maxRelease                      the reserved credit (C_LP) inside it
 *   2 maxRepay                        debt to the external protocol
 *   3 twyneLiqLTV                     the position's CHOSEN threshold, 1e4
 *   4 canLiquidate
 *   5 isExternallyLiquidated          when true, every action reverts
 *   6 canRebalance                    REVERTS when there is nothing to release
 *
 * The receipt token and its share price are NOT in this batch: the vault does
 * not implement `convertToAssets` (neither ERC-20 nor ERC-4626 —
 * `T_CV_OperationDisabled`), so it has to be asked of the RECEIPT, and this
 * batch is the wrong place to ask. It is sharded across endpoints with per-call
 * failures returned as `null`, and the parser cannot tell a lost share-price
 * read from a genuine 1:1 — it would silently understate every position by the
 * receipt's accrued yield. The discovery round resolves it instead.
 */
export async function buildTwyneUserCall(
  chainId: string,
  lender: string,
  account: string,
): Promise<Call[]> {
  const { vaults } = await discover(chainId, lender, account)
  if (vaults.length === 0) return []

  return vaults.flatMap(({ vault }) => [
    { address: vault, name: 'totalAssetsDepositedOrReserved', args: [] },
    { address: vault, name: 'maxRelease', args: [] },
    { address: vault, name: 'maxRepay', args: [] },
    { address: vault, name: 'twyneLiqLTV', args: [] },
    { address: vault, name: 'canLiquidate', args: [] },
    { address: vault, name: 'isExternallyLiquidated', args: [] },
    { address: vault, name: 'canRebalance', args: [] },
  ]) as Call[]
}
